/**
 * Multi-Location Post Creator Component
 * 
 * Allows creating posts for multiple GMB locations at once
 * with franchise grouping and bulk selection
 */

import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { 
  X,
  Building2,
  Send,
  Save,
  Plus,
  Tag,
  AlertCircle
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { publishPost, validatePost } from '@/utils/gmbPublish'
import type { 
  GmbLocation,
  GmbPostTopicType,
  GmbCallToActionType,
  MediaItem,
  CreatePostInput
} from '@/types/gmb'

interface MultiLocationPostCreatorProps {
  locations: GmbLocation[]
  groupedLocations: Record<string, GmbLocation[]>
  onClose: () => void
}

export function MultiLocationPostCreator({
  locations,
  groupedLocations,
  onClose
}: MultiLocationPostCreatorProps) {
  // Form state
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [postType, setPostType] = useState<GmbPostTopicType>('STANDARD')
  const [summary, setSummary] = useState('')
  const [ctaType, setCtaType] = useState<GmbCallToActionType | undefined>()
  const [ctaUrl, setCtaUrl] = useState('')
  const [mediaUrls, setMediaUrls] = useState<MediaItem[]>([])
  const [isScheduled, setIsScheduled] = useState(false)
  const [scheduledDate, setScheduledDate] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  
  // Event-specific state
  const [eventTitle, setEventTitle] = useState('')
  const [eventStartDate, setEventStartDate] = useState('')
  const [eventEndDate, setEventEndDate] = useState('')
  
  // Offer-specific state
  const [offerCouponCode, setOfferCouponCode] = useState('')
  const [offerRedeemUrl, setOfferRedeemUrl] = useState('')
  const [offerTerms, setOfferTerms] = useState('')
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [publishResults, setPublishResults] = useState<{
    successful: string[]
    failed: Array<{ locationId: string; error: string }>
  } | null>(null)

  // CTA options based on post type
  const getCtaOptions = (): GmbCallToActionType[] => {
    switch (postType) {
      case 'EVENT':
        return ['LEARN_MORE', 'RESERVE', 'SIGN_UP', 'CALL']
      case 'OFFER':
        return ['GET_OFFER', 'SHOP', 'ORDER', 'CALL']
      case 'PRODUCT':
        return ['ORDER', 'SHOP', 'LEARN_MORE', 'CALL']
      default:
        return ['LEARN_MORE', 'BOOK', 'CALL', 'SIGN_UP']
    }
  }

  // Handle location selection
  const handleLocationToggle = (locationId: string) => {
    setSelectedLocations(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId)
      } else {
        return [...prev, locationId]
      }
    })
  }

  // Handle franchise group selection
  const handleFranchiseToggle = (franchise: string) => {
    const franchiseLocations = groupedLocations[franchise] || []
    const franchiseLocationIds = franchiseLocations.map(l => l.gmb_location_id)
    
    const allSelected = franchiseLocationIds.every(id => selectedLocations.includes(id))
    
    if (allSelected) {
      // Deselect all
      setSelectedLocations(prev => prev.filter(id => !franchiseLocationIds.includes(id)))
    } else {
      // Select all
      setSelectedLocations(prev => {
        const newSelection = [...prev]
        franchiseLocationIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id)
          }
        })
        return newSelection
      })
    }
  }

  // Check if all locations in a franchise are selected
  const isFranchiseSelected = (franchise: string): boolean => {
    const franchiseLocations = groupedLocations[franchise] || []
    const franchiseLocationIds = franchiseLocations.map(l => l.gmb_location_id)
    return franchiseLocationIds.length > 0 && franchiseLocationIds.every(id => selectedLocations.includes(id))
  }

  // Check if some locations in a franchise are selected
  const isFranchisePartiallySelected = (franchise: string): boolean => {
    const franchiseLocations = groupedLocations[franchise] || []
    const franchiseLocationIds = franchiseLocations.map(l => l.gmb_location_id)
    const selectedCount = franchiseLocationIds.filter(id => selectedLocations.includes(id)).length
    return selectedCount > 0 && selectedCount < franchiseLocationIds.length
  }

  // Handle media addition
  const handleAddMedia = () => {
    const newMedia: MediaItem = {
      mediaFormat: 'PHOTO',
      sourceUrl: '',
      description: ''
    }
    setMediaUrls([...mediaUrls, newMedia])
  }

  // Handle media removal
  const handleRemoveMedia = (index: number) => {
    setMediaUrls(mediaUrls.filter((_, i) => i !== index))
  }

  // Build post draft
  const buildPostDraft = (locationId: string): CreatePostInput => {
    const post: CreatePostInput = {
      gmb_location_id: locationId,
      language_code: 'en',
      summary: summary.trim(),
      topic_type: postType,
      cta_type: ctaType,
      cta_url: ctaUrl,
      media_urls: mediaUrls.filter(m => m.sourceUrl),
      scheduled_for: isScheduled && scheduledDate && scheduledTime
        ? new Date(`${scheduledDate}T${scheduledTime}`)
        : undefined
    }

    // Add event-specific fields
    if (postType === 'EVENT') {
      post.event_title = eventTitle
      post.event_start_date = eventStartDate ? new Date(eventStartDate) : undefined
      post.event_end_date = eventEndDate ? new Date(eventEndDate) : undefined
    }

    // Add offer-specific fields
    if (postType === 'OFFER') {
      post.offer_coupon_code = offerCouponCode
      post.offer_redeem_url = offerRedeemUrl
      post.offer_terms = offerTerms
    }

    return post
  }

  // Handle form submission
  const handleSubmit = async (isDraft = false) => {
    try {
      setError(null)
      setIsSubmitting(true)
      setPublishResults(null)

      // Validate
      if (selectedLocations.length === 0) {
        setError('Please select at least one location')
        return
      }

      // Validate post content
      const sampleDraft = buildPostDraft(selectedLocations[0])
      const validation = validatePost({
        locationId: selectedLocations[0],
        summary: sampleDraft.summary,
        topicType: sampleDraft.topic_type,
        callToAction: sampleDraft.cta_type ? {
          actionType: sampleDraft.cta_type,
          url: sampleDraft.cta_url
        } : undefined,
        mediaUrls: sampleDraft.media_urls?.map(m => m.sourceUrl).filter(Boolean) || [],
        event: sampleDraft.event_title ? {
          title: sampleDraft.event_title,
          startDate: sampleDraft.event_start_date!,
          endDate: sampleDraft.event_end_date
        } : undefined,
        offer: sampleDraft.offer_coupon_code || sampleDraft.offer_redeem_url ? {
          couponCode: sampleDraft.offer_coupon_code,
          redeemUrl: sampleDraft.offer_redeem_url,
          terms: sampleDraft.offer_terms
        } : undefined
      })

      if (!validation.valid) {
        setError(validation.errors.join(', '))
        return
      }

      // Process each location
      const results = {
        successful: [] as string[],
        failed: [] as Array<{ locationId: string; error: string }>
      }

      for (const locationId of selectedLocations) {
        const draft = buildPostDraft(locationId)
        
        try {
          if (isDraft || isScheduled) {
            // Save as draft or scheduled
            const { error } = await supabase
              .from('gmb_posts')
              .insert({
                ...draft,
                state: isScheduled ? 'SCHEDULED' : 'DRAFT',
                created_by: (await supabase.auth.getUser()).data.user?.id
              })
              .select()
              .single()

            if (error) throw error
            results.successful.push(locationId)
          } else {
            // Publish immediately
            const publishDraft = {
              locationId,
              summary: draft.summary,
              topicType: draft.topic_type,
              callToAction: draft.cta_type ? {
                actionType: draft.cta_type,
                url: draft.cta_url
              } : undefined,
              mediaUrls: draft.media_urls?.map(m => m.sourceUrl).filter(Boolean) || [],
              event: draft.event_title ? {
                title: draft.event_title,
                startDate: draft.event_start_date!,
                endDate: draft.event_end_date
              } : undefined,
              offer: draft.offer_coupon_code || draft.offer_redeem_url ? {
                couponCode: draft.offer_coupon_code,
                redeemUrl: draft.offer_redeem_url,
                terms: draft.offer_terms
              } : undefined
            }

            const result = await publishPost(publishDraft)
            
            if (result.success) {
              results.successful.push(locationId)
            } else {
              results.failed.push({
                locationId,
                error: result.error || 'Unknown error'
              })
            }
          }
        } catch (err) {
          results.failed.push({
            locationId,
            error: err instanceof Error ? err.message : 'Unknown error'
          })
        }

        // Add delay between publishes to avoid rate limits
        if (!isDraft && !isScheduled) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }

      setPublishResults(results)

      // If all successful, close the form
      if (results.failed.length === 0) {
        setTimeout(() => {
          onClose()
        }, 2000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Create Multi-Location Post</CardTitle>
            <CardDescription>
              Create a post for multiple GMB locations at once
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Location Selection */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base">Select Locations</Label>
            <Badge variant="secondary">
              {selectedLocations.length} selected
            </Badge>
          </div>
          
          <div className="space-y-4 max-h-64 overflow-y-auto border rounded-lg p-4">
            {Object.entries(groupedLocations).map(([franchise, locs]) => (
              <div key={franchise} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`franchise-${franchise}`}
                    checked={isFranchiseSelected(franchise)}
                    onCheckedChange={() => handleFranchiseToggle(franchise)}
                    className={isFranchisePartiallySelected(franchise) ? 'data-[state=checked]:bg-blue-500' : ''}
                  />
                  <Label
                    htmlFor={`franchise-${franchise}`}
                    className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                  >
                    <Building2 className="h-4 w-4" />
                    {franchise} ({locs.length})
                  </Label>
                </div>
                
                <div className="ml-6 space-y-1">
                  {locs.map(location => (
                    <div key={location.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`location-${location.id}`}
                        checked={selectedLocations.includes(location.gmb_location_id)}
                        onCheckedChange={() => handleLocationToggle(location.gmb_location_id)}
                      />
                      <Label
                        htmlFor={`location-${location.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {location.location_name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Post Type Selection */}
        <Tabs value={postType} onValueChange={(v) => setPostType(v as GmbPostTopicType)}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="STANDARD">Standard</TabsTrigger>
            <TabsTrigger value="EVENT">Event</TabsTrigger>
            <TabsTrigger value="OFFER">Offer</TabsTrigger>
            <TabsTrigger value="PRODUCT">Product</TabsTrigger>
          </TabsList>

          {/* Standard Post */}
          <TabsContent value="STANDARD" className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Standard posts are great for updates, announcements, and general content.
              </AlertDescription>
            </Alert>
          </TabsContent>

          {/* Event Post */}
          <TabsContent value="EVENT" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="event-title">Event Title *</Label>
                <Input
                  id="event-title"
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  placeholder="Spring Masonry Workshop"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="event-start">Start Date</Label>
                  <Input
                    id="event-start"
                    type="date"
                    value={eventStartDate}
                    onChange={(e) => setEventStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="event-end">End Date</Label>
                  <Input
                    id="event-end"
                    type="date"
                    value={eventEndDate}
                    onChange={(e) => setEventEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Offer Post */}
          <TabsContent value="OFFER" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="coupon-code">Coupon Code</Label>
                <Input
                  id="coupon-code"
                  value={offerCouponCode}
                  onChange={(e) => setOfferCouponCode(e.target.value)}
                  placeholder="SPRING2024"
                />
              </div>
              
              <div>
                <Label htmlFor="redeem-url">Redemption URL</Label>
                <Input
                  id="redeem-url"
                  value={offerRedeemUrl}
                  onChange={(e) => setOfferRedeemUrl(e.target.value)}
                  placeholder="https://example.com/offer"
                />
              </div>
              
              <div>
                <Label htmlFor="offer-terms">Terms & Conditions</Label>
                <Textarea
                  id="offer-terms"
                  value={offerTerms}
                  onChange={(e) => setOfferTerms(e.target.value)}
                  placeholder="Valid until..."
                  rows={3}
                />
              </div>
            </div>
          </TabsContent>

          {/* Product Post */}
          <TabsContent value="PRODUCT" className="space-y-4">
            <Alert>
              <Tag className="h-4 w-4" />
              <AlertDescription>
                Showcase your masonry services and products with engaging content.
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>

        {/* Post Content */}
        <div className="space-y-2">
          <Label htmlFor="summary">Post Content *</Label>
          <Textarea
            id="summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="What's happening at your business?"
            rows={6}
            className="resize-none"
          />
          <div className="text-xs text-muted-foreground text-right">
            {summary.length}/1500 characters
          </div>
        </div>

        {/* Media Upload */}
        <div className="space-y-2">
          <Label>Media</Label>
          <div className="space-y-2">
            {mediaUrls.map((media, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={media.sourceUrl}
                  onChange={(e) => {
                    const updated = [...mediaUrls]
                    updated[index].sourceUrl = e.target.value
                    setMediaUrls(updated)
                  }}
                  placeholder="Image URL"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveMedia(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddMedia}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Image
            </Button>
          </div>
        </div>

        {/* Call to Action */}
        <div className="space-y-4">
          <Label>Call to Action</Label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cta-type" className="text-xs">Type</Label>
              <select
                id="cta-type"
                value={ctaType || ''}
                onChange={(e) => setCtaType(e.target.value as GmbCallToActionType)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="">None</option>
                {getCtaOptions().map((option) => (
                  <option key={option} value={option}>
                    {option.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            
            {ctaType && (
              <div>
                <Label htmlFor="cta-url" className="text-xs">URL</Label>
                <Input
                  id="cta-url"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
            )}
          </div>
        </div>

        {/* Scheduling */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="schedule">Schedule Post</Label>
            <Switch
              id="schedule"
              checked={isScheduled}
              onCheckedChange={setIsScheduled}
            />
          </div>
          
          {isScheduled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="schedule-date" className="text-xs">Date</Label>
                <Input
                  id="schedule-date"
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="schedule-time" className="text-xs">Time</Label>
                <Input
                  id="schedule-time"
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Results Display */}
        {publishResults && (
          <Alert variant={publishResults.failed.length === 0 ? "default" : "destructive"}>
            <AlertDescription>
              <div className="space-y-2">
                {publishResults.successful.length > 0 && (
                  <div>
                    <strong>Successful:</strong> {publishResults.successful.length} location{publishResults.successful.length !== 1 ? 's' : ''}
                  </div>
                )}
                {publishResults.failed.length > 0 && (
                  <div>
                    <strong>Failed:</strong>
                    <ul className="mt-1 ml-4 list-disc">
                      {publishResults.failed.map(f => {
                        const location = locations.find(l => l.gmb_location_id === f.locationId)
                        return (
                          <li key={f.locationId}>
                            {location?.location_name || f.locationId}: {f.error}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleSubmit(true)}
              disabled={isSubmitting || selectedLocations.length === 0}
            >
              <Save className="h-4 w-4 mr-1" />
              Save Draft
            </Button>
            <Button
              onClick={() => handleSubmit(false)}
              disabled={isSubmitting || selectedLocations.length === 0 || !summary.trim()}
            >
              <Send className="h-4 w-4 mr-1" />
              {isScheduled ? 'Schedule Post' : 'Publish Now'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default MultiLocationPostCreator